<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useNotificationStore } from '../stores/notificationStore'

const notificationStore = useNotificationStore()
const visible = ref(false)
const currentNotification = ref(null)
const isCompact = ref(false)
const countdown = ref(0)
let timeoutId = null
let compactTimeoutId = null
let countdownInterval = null

// Watch for new notifications
const unreadNotifications = computed(() => notificationStore.unreadNotifications)

// Show toast when new notification arrives
const showToast = (notification) => {
  if (notification?.category === 'specials') {
    return
  }
  if (timeoutId) {
    clearTimeout(timeoutId)
  }
  if (compactTimeoutId) {
    clearTimeout(compactTimeoutId)
  }
  
  currentNotification.value = notification
  visible.value = true
  isCompact.value = false
  
  // Auto-hide after different durations based on type
  // Progress notifications don't auto-hide, others hide after 4 seconds
  if (notification.type !== 'progress') {
    timeoutId = setTimeout(() => {
      visible.value = false
      currentNotification.value = null
    }, 4000)
  } else {
    // For progress notifications, go compact after 5 seconds
    compactTimeoutId = setTimeout(() => {
      isCompact.value = true
    }, 5000)
    
    // If progress is 100%, auto-close after 5 seconds with countdown
    if (notification.progress === 100) {
      startCountdown(5)
    }
  }
}

// Watch for new unread notifications
const lastNotificationCount = ref(0)
const checkForNewNotifications = () => {
  const currentCount = unreadNotifications.value.length
  
  if (currentCount > lastNotificationCount.value && currentCount > 0) {
    // New notification arrived, show the latest one
    const latestNotification = unreadNotifications.value[0]
    showToast(latestNotification)
  }
  
  lastNotificationCount.value = currentCount
}

// Use a watcher to detect new notifications
import { watch } from 'vue'
watch(unreadNotifications, () => {
  checkForNewNotifications()
}, { immediate: true })

// Watch for progress changes to handle 100% auto-close
watch(() => currentNotification.value?.progress, (newProgress, oldProgress) => {
  if (newProgress === 100 && oldProgress < 100 && currentNotification.value?.type === 'progress') {
    // Clear any existing timeout
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    // Auto-close after 5 seconds when reaching 100% with countdown
    startCountdown(5)
  }
})

const startCountdown = (seconds) => {
  countdown.value = seconds
  
  // Clear any existing countdown
  if (countdownInterval) {
    clearInterval(countdownInterval)
  }
  
  countdownInterval = setInterval(() => {
    countdown.value--
    if (countdown.value <= 0) {
      clearInterval(countdownInterval)
      visible.value = false
      currentNotification.value = null
      isCompact.value = false
      countdown.value = 0
    }
  }, 1000)
}

const closeToast = () => {
  visible.value = false
  currentNotification.value = null
  isCompact.value = false
  countdown.value = 0
  
  if (timeoutId) {
    clearTimeout(timeoutId)
  }
  if (compactTimeoutId) {
    clearTimeout(compactTimeoutId)
  }
  if (countdownInterval) {
    clearInterval(countdownInterval)
  }
}

const getNotificationIcon = (type) => {
  switch (type) {
    case 'success':
      return 'fa-check-circle'
    case 'warning':
      return 'fa-exclamation-triangle'
    case 'error':
      return 'fa-times-circle'
    case 'progress':
      return 'fa-spinner fa-spin'
    case 'info':
    default:
      return 'fa-info-circle'
  }
}

const getNotificationColor = (type) => {
  switch (type) {
    case 'success':
      return 'bg-green-500'
    case 'warning':
      return 'bg-yellow-500'
    case 'error':
      return 'bg-red-500'
    case 'progress':
      return 'bg-purple-500'
    case 'info':
    default:
      return 'bg-blue-500'
  }
}

onUnmounted(() => {
  if (timeoutId) {
    clearTimeout(timeoutId)
  }
  if (compactTimeoutId) {
    clearTimeout(compactTimeoutId)
  }
  if (countdownInterval) {
    clearInterval(countdownInterval)
  }
})
</script>

<template>
  <teleport to="body">
    <transition
      enter-active-class="transition-all duration-300 ease-out"
      leave-active-class="transition-all duration-300 ease-in"
      enter-from-class="opacity-0 translate-x-full"
      enter-to-class="opacity-100 translate-x-0"
      leave-from-class="opacity-100 translate-x-0"
      leave-to-class="opacity-0 translate-x-full"
    >
      <div
        v-if="visible && currentNotification"
        class="fixed top-4 right-4 z-50 max-w-md w-full"
      >
        <!-- Compact Mode (after 5 seconds for progress notifications) -->
        <div v-if="isCompact && currentNotification.type === 'progress'" class="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
          <div class="flex items-center p-3">
            <!-- Compact Icon -->
            <div class="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white bg-purple-500">
              <i class="fas fa-spinner fa-spin text-xs"></i>
            </div>
            
            <!-- Compact Content -->
            <div class="ml-3 flex-1 min-w-0">
              <div class="flex items-center justify-between">
                <p class="text-sm font-medium text-gray-900 truncate">
                  {{ currentNotification.message }}
                </p>
                <div class="flex items-center gap-2">
                  <span class="text-xs text-gray-500">
                    {{ Math.round(currentNotification.progress) }}%
                  </span>
                  <span 
                    v-if="currentNotification.progress === 100"
                    class="text-xs text-green-600 font-medium"
                    :title="`Will auto-close in ${countdown} seconds`"
                  >
                    <i class="fas fa-check-circle mr-1"></i>
                    Complete
                    <span v-if="countdown > 0" class="ml-1 text-orange-600">
                      ({{ countdown }}s)
                    </span>
                  </span>
                </div>
              </div>
              
              <!-- Compact Progress Bar -->
              <div class="mt-2 w-full bg-gray-200 rounded-full h-1.5 relative overflow-hidden">
                <div 
                  class="bg-purple-500 h-1.5 rounded-full transition-all duration-300 ease-out"
                  :style="{ width: `${currentNotification.progress}%` }"
                ></div>
                <!-- Loading animation overlay -->
                <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 loading-shimmer"></div>
              </div>
            </div>
            
            <!-- Compact Action Buttons -->
            <div class="ml-2 flex items-center gap-1">
              <button
                @click="isCompact = false"
                class="flex-shrink-0 text-purple-600 hover:text-purple-800 focus:outline-none text-xs font-medium"
                title="See Details"
              >
                <i class="fas fa-expand-alt mr-1"></i>
                Details
              </button>
              <button
                @click="closeToast"
                class="flex-shrink-0 text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                <i class="fas fa-times text-xs"></i>
              </button>
            </div>
          </div>
        </div>
        
        <!-- Full Mode (default) -->
        <div v-else class="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
          <!-- Header with icon and close button -->
          <div class="flex items-start p-4">
            <div 
              class="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white"
              :class="getNotificationColor(currentNotification.type)"
            >
              <i :class="`fas ${getNotificationIcon(currentNotification.type)} text-sm`"></i>
            </div>
            
            <div class="ml-3 flex-1">
              <div class="flex items-start justify-between">
                <div class="flex-1 min-w-0">
                  <!-- Notification Type Badge -->
                  <div class="flex items-center gap-2 mb-1">
                    <span 
                      v-if="currentNotification.type"
                      class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                      :class="{
                        'bg-green-100 text-green-800': currentNotification.type === 'success',
                        'bg-yellow-100 text-yellow-800': currentNotification.type === 'warning',
                        'bg-red-100 text-red-800': currentNotification.type === 'error',
                        'bg-purple-100 text-purple-800': currentNotification.type === 'progress',
                        'bg-blue-100 text-blue-800': currentNotification.type === 'info'
                      }"
                    >
                      {{ currentNotification.type.toUpperCase() }}
                    </span>
                    <span 
                      v-if="currentNotification.category && currentNotification.category !== 'system'"
                      class="text-xs text-gray-500"
                    >
                      {{ currentNotification.category }}
                    </span>
                  </div>
                  
                  <!-- Message -->
                  <p class="text-sm font-medium text-gray-900 leading-relaxed">
                    {{ currentNotification.message }}
                  </p>
                  
                  <!-- Progress Bar for progress type -->
                  <div v-if="currentNotification.type === 'progress' && currentNotification.progress !== undefined" class="mt-3">
                    <div class="flex justify-between text-xs text-gray-600 mb-1">
                      <span>Progress</span>
                      <span>{{ Math.round(currentNotification.progress) }}%</span>
                    </div>
                    <div class="w-full bg-gray-200 rounded-full h-2 relative overflow-hidden">
                      <div 
                        class="bg-purple-500 h-2 rounded-full transition-all duration-300 ease-out"
                        :style="{ width: `${currentNotification.progress}%` }"
                      ></div>
                      <!-- Loading animation overlay -->
                      <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 loading-shimmer"></div>
                      <!-- Persistent indicator -->
                      <div class="absolute -top-1 -right-1 w-3 h-3 bg-purple-500 rounded-full flex items-center justify-center">
                        <i class="fas fa-pause text-white text-xs"></i>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Timestamp and Source -->
                  <div class="flex items-center justify-between mt-2">
                    <p class="text-xs text-gray-500">
                      {{ currentNotification.time }}
                    </p>
                    <div class="flex items-center gap-2">
                      <p 
                        v-if="currentNotification.source === 'sse'"
                        class="text-xs text-green-600 font-medium"
                      >
                        <i class="fas fa-broadcast-tower mr-1"></i>
                        Live
                      </p>
                      <p 
                        v-if="currentNotification.type === 'progress' && currentNotification.progress < 100"
                        class="text-xs text-purple-600 font-medium"
                      >
                        <i class="fas fa-pause mr-1"></i>
                        Persistent
                      </p>
                      <p 
                        v-if="currentNotification.type === 'progress' && currentNotification.progress === 100"
                        class="text-xs text-green-600 font-medium"
                        :title="`Will auto-close in ${countdown} seconds`"
                      >
                        <i class="fas fa-check-circle mr-1"></i>
                        Complete
                        <span v-if="countdown > 0" class="ml-1 text-orange-600">
                          ({{ countdown }}s)
                        </span>
                      </p>
                    </div>
                  </div>
                  
                  <!-- Additional Details -->
                  <div 
                    v-if="currentNotification.details && Object.keys(currentNotification.details).length > 0"
                    class="mt-2 text-xs text-gray-600 bg-gray-50 p-2 rounded border"
                  >
                    <details>
                      <summary class="cursor-pointer font-medium">Details</summary>
                      <pre class="mt-1 text-xs overflow-auto">{{ JSON.stringify(currentNotification.details, null, 2) }}</pre>
                    </details>
                  </div>
                </div>
                
                <div class="ml-2 flex items-center gap-2">
                  <button
                    v-if="currentNotification.type === 'progress'"
                    @click="isCompact = true"
                    class="flex-shrink-0 text-purple-600 hover:text-purple-800 focus:outline-none text-xs font-medium"
                    title="Minimize"
                  >
                    <i class="fas fa-compress-alt mr-1"></i>
                    Minimize
                  </button>
                  <button
                    @click="closeToast"
                    class="flex-shrink-0 text-gray-400 hover:text-gray-600 focus:outline-none"
                  >
                    <i class="fas fa-times text-sm"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Progress bar for auto-hide (only for non-progress notifications) -->
          <div v-if="currentNotification?.type !== 'progress'" class="h-1 bg-gray-200">
            <div 
              class="h-full bg-gray-400 transition-all ease-linear"
              :class="visible ? 'w-0' : 'w-full'"
              style="animation: progress 4s linear forwards;"
            ></div>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<style scoped>
@keyframes progress {
  from {
    width: 100%;
  }
  to {
    width: 0%;
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.loading-shimmer {
  animation: shimmer 2s infinite;
}
</style>